/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { connect, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import { bindActionCreators, Dispatch } from "redux";

import { PrimaryButton, SearchBox, Stack } from "@fluentui/react";

import * as BenefitConstants from "Constants/BenefitConstants";
import * as BenefitActions from "Dataflows/Benefit/BenefitAction";
import * as AwardKitSkuAction from "Dataflows/AwardKitSku/AwardKitSkuAction";

import BenefitsList from "Components/BenefitsList/BenefitsList";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { ResultCount } from "Components/Common/ListCounts/ListCounts.styles";
import { Title } from "Components/Common/Results/Result.styles";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import {
  getBenefits,
  getBenefitsFilterValues,
  getCurrentBenefitName,
  getCurrentPage,
  getCurrentTenant,
  getFilteredCount,
  getLoadBenefitsError,
  getPageSize,
  getSearchText,
  getUnclaimedCount,
  isLoadingBenefits,
} from "Dataflows/Benefit/BenefitSelector";
import { AppState } from "Dataflows/Reducers";

import { IFilterValue } from "Interfaces/IFilterValue";
import { IFilterField } from "Interfaces/IFilterField";

import { IBenefitsContainerMatchProps, IBenefitsContainerProps } from "./IBenefitsContainerProps";
import { useGetBenefitsQuery } from "Services/BenefitManagementApi";

const BenefitsContainer: React.FunctionComponent<IBenefitsContainerProps> = (
  props: IBenefitsContainerProps
): ReactElement => {
  const {
    benefits,
    isLoadingBenefits,
    loadBenefitsError,
    currentPage,
    pageSize,
    filteredCount,
    unclaimedCount,
    benefitsFilterValues,
    currentTenant,
    currentBenefitName,
    actions: {
      searchBenefits,
      updateCurrentPage,
      updatePageSize,
      addBenefitsFilterValue,
      updateBenefitsFilterValue,
      removeBenefitsFilterValue,
      resetBenefits,
    },
  } = props;

  // get dynamic benfilt's navigation links response
  const { data } = useGetBenefitsQuery({});

  const [searchText, setSearchText] = useState(props.searchText);

  const dispatch = useDispatch();
  const history = useHistory();
  const routeParams = useParams<IBenefitsContainerMatchProps>();
  const tenant = SubSiteUrlRootPath[routeParams.tenant];

  //Map Route Param to BenefitNameEnum
  const benefitNameKey = Object.keys(BenefitConstants.BenefitNameEnum).find(
    (x) => x.toLowerCase() == routeParams.benefitType?.toLowerCase()
  ) as BenefitConstants.BenefitNameEnum;

  let benefitName =
    benefitNameKey == BenefitConstants.BenefitNameEnum.O365
      ? BenefitConstants.BenefitNameEnum.M365
      : BenefitConstants.BenefitNameEnum[benefitNameKey];

  let benefitDisplayName = BenefitConstants.BenefitDisplayNameEnum[benefitNameKey];

  /**
   * Below code is to handle the case when benefitName and benefitDisplayName are not found in the enum/constants.
   * This is to handle the rendering of yaml files in the benefits list page.
   */
  if (!benefitDisplayName && !benefitName) {
    if (tenant === TenantTypesEnum.MVP && data && data.length > 0) {
      const benefit = data.find((benefit) => benefit.id === routeParams.benefitType);
      if (benefit) {
        benefitName = benefit?.name;
        benefitDisplayName = benefit?.navTitle;
      } else {
        history.push("/notFound");
      }
    } else {
      history.push("/notFound");
    }
  }

  const handleSearch = (
    searchValue: string,
    selectedBenefitsFilterValues: IFilterValue[],
    page: number,
    pageSizeLocal: number
  ): void => {
    searchBenefits(
      tenant,
      benefitName,
      searchValue,
      selectedBenefitsFilterValues,
      page,
      pageSizeLocal
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    resetBenefits();

    setSearchText("");
    handleSearch(
      "",
      BenefitConstants.defaultFilterValues,
      1,
      BenefitConstants.defaultListingPageSize
    );

    // Get list of SKU for Award Kit benefit
    if (routeParams.benefitType === BenefitConstants.BenefitsSubSiteUrlPath.awardKit) {
      dispatch(AwardKitSkuAction.getAwardKitSku());
    }
  };

  useEffect(() => {
    if (isLoadingBenefits) return;

    if (tenant !== currentTenant || benefitName !== currentBenefitName) {
      resetFilterValues();
      return;
    }

    handleSearch(searchText, benefitsFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize, tenant, benefitName]);

  const onClear = (): void => {
    handleSearch("", benefitsFilterValues, 1, pageSize);
  };

  const renderUnclaimedCount = (): React.ReactElement => {
    if (isLoadingBenefits) return <ResultCount />;

    return <ResultCount>Unclaimed: {unclaimedCount}</ResultCount>;
  };

  /**
   * Get additional filters for Benefit.
   * O365, Teams and Award Kit
   * @returns Filters based on Benefit
   */
  const getFilterFields = (): IFilterField[] => {
    switch (routeParams.benefitType) {
      case BenefitConstants.BenefitsSubSiteUrlPath.O365:
        return BenefitConstants.benefitO365FilterWithCountryCodePool;
      case BenefitConstants.BenefitsSubSiteUrlPath.MS365:
        return BenefitConstants.benefitMS365FilterWithCountryCodePool;
      case BenefitConstants.BenefitsSubSiteUrlPath.teams:
        return BenefitConstants.benefitTeamsFilterWithCountryCodePool;
      case BenefitConstants.BenefitsSubSiteUrlPath.awardKit:
        return BenefitConstants.benefitAwardKitFilter;
      case BenefitConstants.BenefitsSubSiteUrlPath.swagKit:
        return BenefitConstants.benefitSwagKitFilter;
      default:
        return BenefitConstants.benefitFilterFields;
    }
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addBenefitsFilterValue}
          removeFilterValue={removeBenefitsFilterValue}
          updateFilterValue={updateBenefitsFilterValue}
          selectedFilterValues={benefitsFilterValues}
          filterFields={getFilterFields()}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void => handleSearch(searchText, benefitsFilterValues, 1, pageSize)}
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Benefits - {benefitDisplayName}</Title>
            <ListCounts total={filteredCount} loading={isLoadingBenefits} />
            &nbsp; &nbsp; &nbsp;
            {renderUnclaimedCount()}
          </PaddedFlexRow>
          <SearchBox
            placeholder="Search benefits"
            value={searchText}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, benefitsFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchText(newValue || "")
            }
            onClear={onClear}
            styles={searchBoxStyles}
          />
          <BenefitsList
            data={benefits}
            filteredCount={filteredCount}
            pageSize={pageSize}
            isLoadingBenefits={isLoadingBenefits}
            currentPage={currentPage}
            loadBenefitsError={loadBenefitsError}
            hasSwagKitCol={BenefitConstants.additionalFilter.swagKit.includes(
              routeParams.benefitType
            )}
            hasCountryCodePoolCol={BenefitConstants.additionalFilter.countryCodePool.includes(
              routeParams.benefitType
            )}
            hasSkuCol={BenefitConstants.additionalFilter.sku.includes(routeParams.benefitType)}
            benefitName={routeParams.benefitType}
            actions={{ updateCurrentPage, updatePageSize }}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

const mapStateToProps = (state: AppState) => ({
  benefits: getBenefits(state.benefitState),
  isLoadingBenefits: isLoadingBenefits(state.benefitState),
  loadBenefitsError: getLoadBenefitsError(state.benefitState),
  currentPage: getCurrentPage(state.benefitState),
  pageSize: getPageSize(state.benefitState),
  searchText: getSearchText(state.benefitState),
  filteredCount: getFilteredCount(state.benefitState),
  unclaimedCount: getUnclaimedCount(state.benefitState),
  benefitsFilterValues: getBenefitsFilterValues(state.benefitState),
  currentTenant: getCurrentTenant(state.benefitState),
  currentBenefitName: getCurrentBenefitName(state.benefitState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<BenefitActions.BenefitActionTypes>) => ({
  actions: {
    searchBenefits: bindActionCreators(BenefitActions.searchBenefits, dispatch),
    updateCurrentPage: bindActionCreators(BenefitActions.updateCurrentPage, dispatch),
    updatePageSize: bindActionCreators(BenefitActions.updatePageSize, dispatch),
    addBenefitsFilterValue: bindActionCreators(BenefitActions.addBenefitsFilterValue, dispatch),
    updateBenefitsFilterValue: bindActionCreators(
      BenefitActions.updateBenefitsFilterValue,
      dispatch
    ),
    removeBenefitsFilterValue: bindActionCreators(
      BenefitActions.removeBenefitsFilterValue,
      dispatch
    ),
    resetBenefits: bindActionCreators(BenefitActions.resetBenefits, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BenefitsContainer);
